package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.BOOK_COVER_MAX_WIDTH
import com.corbettcode.allaboutrocks.FIGURE_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HERO_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.components.widgets.Figure
import com.corbettcode.allaboutrocks.pages.LearnBookRatingStyle
import com.corbettcode.allaboutrocks.pages.LearnBookTitleStyle
import com.corbettcode.allaboutrocks.toSitePalette
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
//private val HERO_IMAGE_MAX_WIDTH = 400.px

private val PRESS_IMAGE_MAX_HEIGHT = 800.px
private val PRESS_IMAGE_MAX_WIDTH = 900.px
private val PRESS_IMAGE_SMALLER_MAX_WIDTH = 600.px
private val PRESS_IMAGE_VERTICAL_MARGIN = 24.px

private val PROGRAM_POSTER_IMAGE_MAX_HEIGHT = 800.px
private val PROGRAM_POSTER_IMAGE_MAX_WIDTH = 400.px
private val PROGRAM_POSTER_IMAGE_VERTICAL_MARGIN = 24.px

@InitRoute
fun initTypesOfJasper(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

/** Named jasper varieties, paired with what sets each one apart. */
private val jasperVarieties = listOf(
    "Red Jasper" to "Deep brick to blood red from iron oxide; the most common and historically the most widely carved variety."
    , "Picture Jasper" to "Tan and brown bands that resemble desert landscapes, formed as fine sediment settled in silica-rich mud."
    , "Ocean Jasper" to "Orb-like circular patterns in green, pink, and white, found in coastal deposits in Madagascar."
    , "Mookaite" to "Australian jasper in mustard yellow, cream, and burgundy, often mottled within a single stone."
    , "Bloodstone (Heliotrope)" to "Dark green jasper flecked with red iron oxide spots."
    , "Brecciated Jasper" to "Angular fragments of broken jasper recemented by quartz, giving a shattered, mosaic look."
    , "Poppy Jasper" to "Red or orange with rounded flower-like spots caused by gas bubbles in the original volcanic rock."
    , "Leopard Skin Jasper" to "Tan and pink rosettes ringed with darker rims, resembling spotted animal hide."
    , "Dalmatian Jasper" to "Cream colored with black spots; technically a feldspar-rich rock rather than true jasper."
    , "Green Jasper" to "Colored by chlorite or iron silicates; prized in antiquity for seals and signet rings."
)

private val jasperKeyCharacteristics = listOf(
    "Hardness" to "6.5–7 on the Mohs scale, making it durable and suitable for carving and jewelry."
    , "Fracture" to "Conchoidal (smooth, curved breaks), though less glassy than pure quartz."
    , "Luster" to "Waxy to dull, sometimes greasy."
    , "Opacity" to "Generally opaque, though some specimens may be slightly translucent at edges."
)

/** Outside references, paired with what each one is actually good for. */
private val jasperFurtherReading = listOf(
    Triple(
        "Wikipedia: Jasper"
        , "The general overview — composition, varieties, and the long history of the stone, with sources worth following."
        , "https://en.wikipedia.org/wiki/Jasper"
    )
    , Triple(
        "Mindat: Jasper"
        , "Mineral data and a locality database recording where jasper has actually been found. Note that Mindat uses the name more strictly than the lapidary trade does."
        , "https://www.mindat.org/min-2082.html"
    )
    , Triple(
        "Geology.com: Flint, Chert, and Jasper"
        , "Sorts out the names given to microcrystalline quartz, which is where most of the confusion about what counts as jasper starts."
        , "https://geology.com/rocks/flint-chert-jasper/"
    )
    , Triple(
        "Minerals.net: Jasper"
        , "A variety-by-variety breakdown, handy when you are trying to put a name to a particular color or pattern."
        , "https://www.minerals.net/mineral/jasper.aspx"
    )
    , Triple(
        "Wikipedia: Heliotrope (bloodstone)"
        , "A closer look at the dark green, red-flecked variety listed above, and at how long it has been carved."
        , "https://en.wikipedia.org/wiki/Heliotrope_(mineral)"
    )
)

@Composable
private fun PetrifiedWoodFigure(
    source: String
    , alt: String
    , caption: String
    , credit: String
) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(0.5.cssRem)
        , horizontalAlignment = CenterHorizontally
    ) {
        Image(
            src = source
            , alt = alt
            , modifier = Modifier
                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
        P { Em { Text(caption) } }
        SpanText(credit, ImageCreditStyle.toModifier())
    }
}

@Composable
private fun TypesOfJasperHeadline() {
    val sitePalette = ColorMode.current.toSitePalette()

    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Types of Jasper"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfJasperHero() {

    TypesOfJasperHeadline()

    // 1 column on mobile, 2 columns on md+ (text left, image right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/jasper/different_types_of_jasper_poster.png"
                    , alt = "how to identify rocks"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Jasper is a dense, opaque, microcrystalline variety of quartz (SiO₂), prized for its rich colors and smooth, waxy luster. Unlike translucent gemstones such as amethyst or clear quartz, jasper is opaque and typically forms in massive, fine-grained masses rather than distinct crystals.")
            }

            SpanText(
                "How It Forms"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = Alignment.Start)
            )
            P {
                Text("Jasper often originates in sedimentary, volcanic, or metamorphic environments. Over long periods, silica-rich fluids infiltrate cracks and voids in rock, depositing quartz in microscopic fibrous or granular form. Impurities—iron oxides, manganese, and organic matter—give jasper its wide range of colors, including red, yellow, brown, green, blue, and gray. Banded jasper, such as jasper with alternating layers, can resemble agate but remains opaque.")
            }

         }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        SpanText(
            "Key Characteristics"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )

        Ul(PageListStyle.toAttrs()) {
            jasperKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        HorizontalDivider(Modifier
            .fillMaxWidth()
            .margin(topBottom = 2.cssRem)
        )

        Figure(
            source = "/images/typesof/jasper/Red-Jasper.webp"
            , alt = "red jasper"
            , caption = "Rough Red jasper from Salish Sea, Canada"
            , credit = "Photo: Coolbreezeagates/IG"
        )

        // -------------------------- \\
        // --- type and varieties --- \\
        // -------------------------- \\
        SpanText(
            "Types and Varieties"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Jasper has many named varieties based on color, pattern, or origin:")
        }

        Ul(PageListStyle.toAttrs()) {
            jasperVarieties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        SpanText(
            "Historical and Cultural Uses",
            PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Jasper has been used since prehistoric times for tools, weapons, and ornaments. In ancient Egypt, it was carved into amulets and scarabs. Native American cultures used it for arrowheads and ceremonial objects. In folklore, jasper was considered a protective stone, associated with grounding, courage, and vitality.")
        }

        SpanText(
            "Modern Uses",
            PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Today, jasper is popular in jewelry, cabochons, decorative objects, and tumbled stones. Its durability and wide color range make it a favorite among lapidaries and collectors.")
        }

        SpanText(
            "Jasper vs. Agate",
            PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )
        P {
            Text("Both are microcrystalline quartz, but agate is typically translucent and banded, while jasper is opaque. The distinction is not always strict; some stones fall between the two.")
        }

        SpanText(
            "For More Information"
            , PageSectionTitleStyle.toModifier()
                .color(textColor())
                .align(alignment = Alignment.Start)
        )

        Ul(PageListStyle.toAttrs()) {
            jasperFurtherReading.forEach { (title, description, url) ->
                Li {
                    B { Link(url, title) }
                    Text(" — $description")
                }
            }
        }
    }
}

@Composable
private fun TypesOfJasper_BooksSection() {
    val sitePalette = ColorMode.current.toSitePalette()

    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books on Jasper"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )
        }

        CarouselOfSections(
            items = booksOnJasperCarouselSlides
            , frameDuration = 8.seconds
        )
    }
}

@Page("/types_of_rocks/jasper")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfJasperPage() {
    TypesOfJasperHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    TypesOfJasper_BooksSection()
}

/**
 *
 */
val booksOnJasperCarouselSlides = listOf<@Composable () -> Unit>(
    {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agatesjasper.webp"
                , alt = "agates jasper"
                , modifier = Modifier
                    .maxWidth(BOOK_COVER_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column() {
                SpanText(
                    "Collecting Agates and Jaspers of North America"
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
                SpanText(
                    "4.3 on Goodreads"
                    , LearnBookRatingStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )

                P {
                    Text("If you have any interest whatsoever in agates and jasper and if you intend to do some traveling, then this is a good book to have. The author does an impressive job showcasing the material that can be found in most of the US states, as well as parts of Canada and Mexico. I like that the author didn’t use museum grade specimens for her examples, which seems rare in the world of rock books. Instead, she uses good, but still obtainable, rock samples. My only real criticism of the book is that the author lists a price/value of the specimens which was likely inaccurate, even at the time of printing, since the market value of rocks can change rapidly.")
                }
            }
        }
    }
)
